import { EIGHTIES_CATALOG as CATALOG, pickThree as pickThreeFromCatalog } from './eightiesCatalog.js';
// ─── CRUZ'S MOVIE NIGHT — pure logic ────────────────────────────────────────
// Haley, 2026-09-01: a weekly two-player ritual hosted by Cruz in the Pack
// House channel. Every Thursday at 7pm Eastern. Tuesday 3pm Cruz posts three
// 80s picks; the two players vote with a two-key lock; the host pastes a link;
// they watch on a synced player (or a synced clock when embedding is refused);
// both get a ticket-stub keepsake afterwards.
//
// This file is the RULES of that ritual and nothing else: no React, no
// network, no Supabase. Everything here is a pure function of its inputs so
// the schedule, the vote, the URL parsing and the drift math can be tested to
// the second without a browser. The integrator wires these into the channel
// view, the realtime broadcast (see voiceRTC.js for the pattern) and the cron.
//
// WHY THE TIMEZONE WORK IS DONE BY HAND: the night is defined in
// America/New_York wall-clock time ("Thursday 7pm"), but every timestamp we
// store or compare is a UTC instant. Naive `setHours(19)` on a Date uses the
// DEVICE zone — a phone in Nashville or a Vercel cron in UTC would put the
// night an hour or five off, and DST would shift it twice a year. We use
// Intl.DateTimeFormat (built into every runtime we ship to) to read Eastern
// wall-clock parts for an instant and to convert a wall-clock back into an
// instant, so 7pm stays 7pm across the November and March changes.
//
// COPYRIGHT: the catalog below is titles + years ONLY. This module never
// produces a link to a film; it only parses whatever URL the host pastes.

export const MOVIE_NIGHT_TZ = 'America/New_York';
// WHERE MOVIE NIGHT LIVES — currently nowhere, on purpose.
//
// Haley, 2026-10-02: "take the movie night stuff out of the cruz chat".
// This was '45f48c42-12d5-4cf6-b679-a54d71c08b6a', which is #Pack House
// Co-RP! — the Cruz chat — so the ticket-stub card, the player and the living
// room all mounted inside their roleplay, and the cron posted Cruz's picks
// there too. The crons are paused (jobs 22-27) and this unpins the UI: with no
// channel id, `selectedChannelId === MOVIE_NIGHT_CHANNEL_ID` is never true, so
// the card cannot render anywhere.
//
// Nothing else is removed. The movie_nights rows, the catalog, the card, the
// player and the two-key vote are all intact. Give this a channel id and
// re-enable the six cron jobs and Movie Night comes back exactly as it was —
// in whichever channel it is pointed at.
export const MOVIE_NIGHT_CHANNEL_ID = null; // was Pack House Co-RP!; unset until it has its own home
export const MOVIE_NIGHT_HOST_CHARACTER = 'cruz';
// Default projectionist (Haley) — the card falls back to this id, never to a name.
export const MOVIE_NIGHT_DEFAULT_HOST_ID = '6db164e7-0419-470c-a8fe-2ddb899f5578';
export const DEFAULT_FIRST_NIGHT = '2026-09-10T19:00:00-04:00';

// Wall-clock anchors, all Eastern.
const NIGHT_HOUR = 19;        // Thursday 7:00pm
const PICKS_HOUR = 15;        // Tuesday 3:00pm
const REMINDER_HOUR = 18;     // Thursday 6:00pm
const THURSDAY = 4;           // JS getDay(): Sunday = 0
const PICKS_DAYS_BEFORE = 2;  // Thursday − 2 = Tuesday

export const COUNTDOWN_WINDOW_MS = 60 * 60 * 1000;
// A night nobody ever started is not 'countdown' forever. Six hours past the
// scheduled time with no started_at, it's over — the next Tuesday will bring
// new picks. Six, not two: a late start after dinner is still a movie night.
export const MISSED_NIGHT_MS = 6 * 60 * 60 * 1000;

// Sync tolerance. YouTube's getCurrentTime() and <video>.currentTime both
// jitter by a few hundred ms, and broadcast round-trips add more. Seeking on
// every tick would stutter the guest's playback; 1.5s is the smallest band
// that stays invisible in practice while keeping commentary lined up.
export const DRIFT_DEADBAND_S = 1.5;

// ─── Timezone helpers ───────────────────────────────────────────────────────

const etFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: MOVIE_NIGHT_TZ,
  hourCycle: 'h23',
  year: 'numeric', month: 'numeric', day: 'numeric',
  hour: 'numeric', minute: 'numeric', second: 'numeric',
});

function toDate(v) {
  if (v instanceof Date) return v;
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'string') return new Date(v);
  return new Date(NaN);
}

/**
 * Eastern wall-clock parts for an instant: { y, m (1-12), d, h (0-23), mi, s, dow }.
 * `dow` is the Eastern day of week (Sunday = 0), which is what "Thursday" means.
 */
export function etParts(date) {
  const dt = toDate(date);
  const out = {};
  for (const p of etFormatter.formatToParts(dt)) {
    if (p.type === 'year') out.y = Number(p.value);
    else if (p.type === 'month') out.m = Number(p.value);
    else if (p.type === 'day') out.d = Number(p.value);
    else if (p.type === 'hour') out.h = Number(p.value) % 24; // some ICU builds emit "24" at midnight
    else if (p.type === 'minute') out.mi = Number(p.value);
    else if (p.type === 'second') out.s = Number(p.value);
  }
  // Day-of-week of a calendar DATE is zone-independent, so compute it from
  // the Eastern y/m/d via UTC rather than trusting the device zone.
  out.dow = new Date(Date.UTC(out.y, out.m - 1, out.d)).getUTCDay();
  return out;
}

// Eastern offset (ET − UTC, in ms) in effect at a given instant. −4h EDT, −5h EST.
function etOffsetMs(date) {
  const p = etParts(date);
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s);
  return asUtc - toDate(date).getTime();
}

/**
 * Convert an Eastern wall-clock (y, m 1-12, d, h, mi) to a UTC Date. `d` may
 * overflow or underflow (Date.UTC normalises it), which is how "two days
 * before" is done without hand-rolling month lengths.
 *
 * Two-pass: guess the offset from the wall-clock read as if it were UTC,
 * apply it, then re-read the offset at the result. They differ only when the
 * guess straddles a DST change, and the second read is the correct one.
 */
export function etToUtc(y, m, d, h = 0, mi = 0) {
  const guess = Date.UTC(y, m - 1, d, h, mi, 0);
  const off1 = etOffsetMs(guess);
  let utc = guess - off1;
  const off2 = etOffsetMs(utc);
  if (off2 !== off1) utc = guess - off2;
  return new Date(utc);
}

// ─── Schedule ───────────────────────────────────────────────────────────────

/**
 * The next Thursday 7:00pm Eastern at or after `now`, never before firstNight.
 * A `now` exactly at 7:00:00pm counts as tonight; one second after is next week.
 */
export function nextMovieNight(now = new Date(), { firstNight = DEFAULT_FIRST_NIGHT } = {}) {
  const nowDt = toDate(now);
  const first = toDate(firstNight);
  if (Number.isNaN(nowDt.getTime())) throw new TypeError('nextMovieNight: invalid now');
  if (Number.isNaN(first.getTime())) throw new TypeError('nextMovieNight: invalid firstNight');
  if (nowDt.getTime() <= first.getTime()) return first;

  const p = etParts(nowDt);
  // Walk forward day by day from today's Eastern date (at most 7 steps) and
  // take the first Thursday whose 7pm hasn't passed. Building each candidate
  // with etToUtc is what keeps 7pm at 7pm across a DST change mid-week.
  for (let i = 0; i <= 7; i++) {
    const candidate = etToUtc(p.y, p.m, p.d + i, NIGHT_HOUR, 0);
    if (etParts(candidate).dow !== THURSDAY) continue;
    if (candidate.getTime() >= nowDt.getTime()) return candidate;
  }
  // Unreachable: eight consecutive days always contain a Thursday whose 7pm
  // is in the future relative to a `now` on day 0.
  throw new Error('nextMovieNight: no Thursday found');
}

/** Tuesday 3:00pm Eastern before the given night. */
export function picksOpenAt(nightAt) {
  const p = etParts(nightAt);
  return etToUtc(p.y, p.m, p.d - PICKS_DAYS_BEFORE, PICKS_HOUR, 0);
}

/** Thursday 6:00pm Eastern, one hour before the film. */
export function reminderAt(nightAt) {
  const p = etParts(nightAt);
  return etToUtc(p.y, p.m, p.d, REMINDER_HOUR, 0);
}

/**
 * Where a night is in its life. Order matters: explicit start/end markers win
 * over the clock, because a night that started late is still 'live'.
 *
 *   'upcoming'  – before Tuesday 3pm; nothing to do yet
 *   'picks'     – Cruz has posted; voting open (nothing locked)
 *   'locked'    – pick locked; waiting on the host's link and the day
 *   'countdown' – within 60 min of 7pm (or past it, not started, < 6h late)
 *   'live'      – started_at set, no ended_at
 *   'after'     – ended_at set, status 'after'/'cancelled', or missed by 6h+
 *
 * The movie_nights row carries a `status` column that IS the state machine
 * (the tick and the stub RPC both read it), so a row marked 'after' or
 * 'cancelled' is over no matter what its timestamps say — a cancelled week
 * must not keep offering the vote, and a finished one must not read 'live'
 * just because nobody wrote ended_at.
 */
export function phaseFor(now, night) {
  if (!night || !night.night_at) return 'upcoming';
  const t = toDate(now).getTime();
  const nightAt = toDate(night.night_at);
  const nightT = nightAt.getTime();
  if (night.ended_at) return 'after';
  if (night.status === 'after' || night.status === 'cancelled') return 'after';
  if (night.started_at) return 'live';
  if (t >= nightT + MISSED_NIGHT_MS) return 'after';
  if (t < picksOpenAt(nightAt).getTime()) return 'upcoming';
  if (night.locked_idx === null || night.locked_idx === undefined) return 'picks';
  if (t >= nightT - COUNTDOWN_WINDOW_MS) return 'countdown';
  return 'locked';
}

// ─── Two-key vote ───────────────────────────────────────────────────────────

/**
 * Apply one tap to the vote state. Pure: returns new objects, never mutates.
 *
 *   votes    – { [userId]: idx }
 *   userId   – who tapped
 *   idx      – which of the three picks (0-based)
 *   members  – the channel's member ids; taps from anyone else are ignored
 *   locked_idx (optional) – pass the stored value to make a late tap a no-op
 *
 * Rule: nothing locks until BOTH have acted. The first tap is a vote and can
 * be changed freely. The moment the OTHER member taps, the night locks on
 * whatever THEY chose — if they disagree, the second tap wins, because the
 * second player saw the first vote on screen and chose anyway. That is the
 * conversation Haley wants the vote to be: "I see you picked Goonies. Lost
 * Boys." Not a coin flip.
 *
 * On a lock the result also carries the two column-named fields the row needs
 * (`locked_by`, `status: 'locked'`), because the movie-night-tick only flips
 * rows whose status is 'locked' to 'live' at 7pm — a lock that never moved
 * `status` would leave Cruz waiting forever. `lockedBy` is kept as the
 * camelCase twin for callers that read the result rather than persist it.
 *
 * Persisting: the caller must make its UPDATE conditional on the row still
 * being unlocked (`.is('locked_idx', null)`), otherwise two simultaneous taps
 * race and the later write silently overwrites a lock. This function is pure
 * and cannot enforce that.
 */
export function resolveVote(votes, userId, idx, { members = [], locked_idx = null } = {}) {
  const current = { ...(votes || {}) };
  const unchanged = { votes: current, locked_idx: locked_idx ?? null, lockedBy: null };
  if (locked_idx !== null && locked_idx !== undefined) return unchanged;
  if (!members.includes(userId)) return unchanged;
  if (!Number.isInteger(idx) || idx < 0) return unchanged;

  const next = { ...current, [userId]: idx };
  const others = members.filter(m => m !== userId);
  const everyoneElseVoted = others.length > 0 && others.every(m => Number.isInteger(current[m]));
  if (everyoneElseVoted) {
    return { votes: next, locked_idx: idx, lockedBy: userId, locked_by: userId, status: 'locked' };
  }
  return { votes: next, locked_idx: null, lockedBy: null };
}

// ─── Video URL parsing ──────────────────────────────────────────────────────

const YT_ID = /^[A-Za-z0-9_-]{11}$/;
const YT_HOSTS = new Set(['youtube.com', 'youtube-nocookie.com', 'music.youtube.com', 'gaming.youtube.com']);

function stripHost(host) {
  return host.toLowerCase().replace(/^(www|m)\./, '');
}

function safeDecode(s) {
  try { return decodeURIComponent(s); } catch { return s; }
}

/**
 * Classify whatever the host pasted.
 *   { kind: 'youtube', id }              – watch?v=, youtu.be/, shorts/, embed/, live/, v/
 *   { kind: 'archive', id, file? }       – archive.org/details/<id>[/<file>], /download/<id>/<file>
 *   { kind: 'external', url }            – any other http(s) URL (Prime, Netflix, Tubi…)
 *   null                                 – not a URL
 *
 * 'external' is the honest fallback: we can't embed it, so the UI shows the
 * synced clock and both players press play at zero on their own subscription.
 */
export function parseVideoUrl(url) {
  if (typeof url !== 'string') return null;
  let raw = url.trim();
  if (!raw) return null;
  // A bare "netflix.com/..." paste gets https://; anything with its OWN
  // scheme (mailto:, javascript:, ftp:) is left alone so the protocol check
  // below rejects it instead of mangling it into https://mailto:...
  if (!/^[a-z][a-z0-9+.-]*:/i.test(raw)) raw = 'https://' + raw;
  let u;
  try { u = new URL(raw); } catch { return null; }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
  const host = stripHost(u.hostname);
  if (!host.includes('.')) return null;
  const segs = u.pathname.split('/').filter(Boolean);

  if (host === 'youtu.be') {
    const id = segs[0];
    return YT_ID.test(id || '') ? { kind: 'youtube', id } : null;
  }
  if (YT_HOSTS.has(host)) {
    let id = null;
    if (segs[0] === 'watch') id = u.searchParams.get('v');
    else if (['shorts', 'embed', 'live', 'v'].includes(segs[0])) id = segs[1];
    else if (segs.length === 0 && u.searchParams.get('v')) id = u.searchParams.get('v');
    return YT_ID.test(id || '') ? { kind: 'youtube', id } : null;
  }
  if (host === 'archive.org') {
    if ((segs[0] === 'details' || segs[0] === 'download') && segs[1]) {
      const id = safeDecode(segs[1]);
      const file = segs.length > 2 ? safeDecode(segs.slice(2).join('/')) : undefined;
      return file ? { kind: 'archive', id, file } : { kind: 'archive', id };
    }
    return null;
  }
  return { kind: 'external', url: u.href };
}

// ─── Sync ───────────────────────────────────────────────────────────────────

/**
 * How far (seconds) the guest should seek to match the host. The host
 * broadcasts (hostT, hostAt) = its playhead at wall-clock hostAt; the guest
 * projects where the host is NOW and compares with its own playhead.
 *
 * Two call shapes, because MovieNightPlayer.jsx was written in parallel:
 *
 *   syncDrift(hostT, hostAt, nowAt, guestT, { playing, deadband })
 *     → signed seconds. Positive = guest is behind, seek forward. 0 inside
 *       the dead-band.
 *
 *   syncDrift({ hostT, hostAt, now | nowAt, localT | guestT, playing, deadband })
 *     → { drift, inSync: true }            inside the dead-band, leave it
 *     → { drift, seekTo: <seconds> }       outside it, seek to seekTo
 *     This is the shape the Player's driftTarget() reads. It must never
 *     yield a bare 0 in this mode: the Player would take that as "seek to
 *     0:00" and rewind the guest every fifth poll.
 *
 * `playing: false` freezes the projection (a paused host doesn't advance).
 */
export function syncDrift(hostT, hostAt, nowAt, guestT, opts = {}) {
  if (hostT && typeof hostT === 'object' && !(hostT instanceof Date)) {
    const o = hostT;
    const r = driftInner(o.hostT, o.hostAt, o.now ?? o.nowAt, o.localT ?? o.guestT, o);
    if (!r) return { drift: 0, inSync: true };
    return r.drift === 0 ? { drift: 0, inSync: true } : { drift: r.drift, seekTo: r.expected };
  }
  const r = driftInner(hostT, hostAt, nowAt, guestT, opts);
  return r ? r.drift : 0;
}

function driftInner(hostT, hostAt, nowAt, guestT, { playing = true, deadband = DRIFT_DEADBAND_S } = {}) {
  const hostMs = toDate(hostAt).getTime();
  const nowMs = toDate(nowAt).getTime();
  if (![hostT, guestT, hostMs, nowMs].every(Number.isFinite)) return null;
  const elapsed = playing ? Math.max(0, (nowMs - hostMs) / 1000) : 0;
  const expected = hostT + elapsed;
  const raw = expected - guestT;
  const drift = Math.abs(raw) <= deadband ? 0 : raw;
  return { drift, expected };
}

// ─── Keepsake ───────────────────────────────────────────────────────────────

const stubDateFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: MOVIE_NIGHT_TZ, weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
});

/**
 * Ticket-stub keepsake payload (one row per attendee — the integrator adds
 * user_id, since keepsakes RLS is owner-only). Same title/description for
 * both so the stubs match, the way two halves of one ticket would.
 *
 * KEEP IN STEP WITH movie_night_stub() in 20260901180000_movie_nights.sql.
 * The RPC is what actually mints the row; this function is what the card
 * shows on screen before the tap, and MovieNightCard promises they read
 * identically. Same sentence, same flat metadata keys (night_id, title,
 * year, night_at), so the Shelf and the card never disagree.
 */
export function ticketStub(night, { pickTitle, year, attendees = [] } = {}) {
  const nightAt = toDate(night?.night_at);
  const validAt = !Number.isNaN(nightAt.getTime());
  const when = validAt ? stubDateFmt.format(nightAt) : 'one Thursday night';
  const title = (typeof pickTitle === 'string' && pickTitle.trim()) ? pickTitle.trim() : 'Movie Night';
  const film = year ? `${title} (${year})` : title;
  return {
    title: `Ticket stub: ${film}`,
    description: `Cruz's Movie Night, ${when}. One couch, two seats, lights down at 7.`,
    source: 'movie_night',
    metadata: {
      night_id: night?.id ?? null,
      title,
      year: year ?? null,
      night_at: validAt ? nightAt.toISOString() : null,
      attendees: [...attendees],
      // The column is host_user_id (20260901180000_movie_nights.sql); host_id
      // is accepted for callers that already renamed it in memory.
      host_id: night?.host_user_id ?? night?.host_id ?? null,
      video_kind: night?.video?.kind ?? null,
      locked_idx: night?.locked_idx ?? null,
    },
  };
}

/** Convenience: the same stub expanded into insert rows for every attendee. */
export function ticketStubRows(night, info, userIds = []) {
  const stub = ticketStub(night, info);
  return userIds.map(user_id => ({
    user_id,
    character_id: MOVIE_NIGHT_HOST_CHARACTER,
    calendar_id: null,
    ...stub,
  }));
}

// ─── Director notes for the channel AI ──────────────────────────────────────
// These describe the BEAT, never the line. The existing channel AI path
// writes Cruz; we just tell it what is happening in the room.

export function cruzBeatNote(minute, title) {
  const m = Number.isFinite(minute) ? Math.max(0, Math.floor(minute)) : 0;
  const film = title ? `"${title}"` : 'the film';
  let beat;
  if (m === 0) beat = `lights down, opening titles rolling on ${film}. Cruz settles in and sets the mood — one line about why this one, then quiet.`;
  else if (m <= 10) beat = `the opening stretch of ${film} (minute ${m}). Cruz is watching, not narrating: a quick reaction to what's on screen, an aside to whoever's next to him.`;
  else if (m <= 40) beat = `settled in, minute ${m} of ${film}. Cruz reacts to the room as much as the screen — snacks, who's leaning on who, a bit of trivia he's been saving.`;
  else if (m <= 80) beat = `deep in the middle of ${film} (minute ${m}). Something on screen hits close to home; Cruz lets it, briefly, then covers with a joke or a belly laugh.`;
  else if (m <= 110) beat = `the turn — minute ${m} of ${film}, tension rising. Cruz is quieter, invested, maybe a hand finding a shoulder. Short.`;
  else beat = `the closing stretch of ${film} (minute ${m}). Credits are close. Cruz says the thing he says every time the good ones end, and looks at them, not the screen.`;
  return `[DIRECTOR NOTE — MOVIE NIGHT, LIVE] ${beat} Stay under three sentences; the film is the main event, he's company.`;
}

// ─── Catalog ────────────────────────────────────────────────────────────────
// Titles and years only. Cruz picks from this; the host finds the film.

// The catalog lives in src/eightiesCatalog.js (724 films, year-verified —
// Haley: "ALL popular 80s movies not just 40"). Frozen here so nothing in
// the app mutates the shared list.
export const EIGHTIES_CATALOG = Object.freeze(CATALOG);

/**
 * Three picks for a night, chosen deterministically from a seed (use the
 * night's ISO night_at). Deterministic so a cron retry, a second device, or a
 * re-render all agree on the same trio without a round-trip. `exclude` is a
 * list of titles already shown recently so weeks don't repeat.
 */
export function pickThree(seed, { exclude = [], catalog = EIGHTIES_CATALOG } = {}) {
  // Any seed (callers pass the night's ISO string) → a stable 32-bit number,
  // then the shared catalog module does the deterministic draw.
  let h = 2166136261 >>> 0;
  for (const ch of String(seed)) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619) >>> 0; }
  return pickThreeFromCatalog(catalog, { recentTitles: exclude, seed: h });
}

